import { NoddiIcon } from 'noddi-ui-common';
import { commonTexts } from 'noddi-util';
import { useState } from 'react';
import { NoddiIconButton } from '../../Inputs/NoddiIconButton';
import type { NavioNoteAttachment } from './types';

type Translations = typeof commonTexts.notes;

type Props = {
  attachments: NavioNoteAttachment[];
  readOnly?: boolean;
  /**
   * What the host does with the removal: a saved note row deletes it right away (pass
   * `removingIds` so the control spins), a note form stages it until the note is saved.
   * Omit it on read-only surfaces.
   */
  onRemove?: (attachmentId: number) => void;
  /** Attachment ids whose delete request is in flight. */
  removingIds?: ReadonlySet<number>;
  translations?: Partial<Translations>;
};

/**
 * Saved note files: image thumbnails that open the file, and document download links.
 * The backend's `url` is signed and expires, so a thumbnail that 404s falls back to the
 * document icon rather than a broken image; reopening the page fetches fresh URLs.
 * Removal is optional so read-only surfaces (customer, proposals) can reuse the same list.
 */
export const NavioNoteAttachments = ({ attachments, readOnly = false, onRemove, removingIds, translations }: Props) => {
  const texts = { ...commonTexts.notes, ...translations };
  if (attachments.length === 0) {
    return null;
  }

  return (
    <ul className='mt-1 flex flex-wrap gap-2' aria-label={texts.attachments}>
      {attachments.map((attachment) => (
        <li key={attachment.id}>
          {/* Keyed on the url too: a refreshed signed URL remounts the chip, clearing a
              broken-preview fallback learned from the expired one. */}
          <AttachmentChip
            key={`${attachment.id}:${attachment.url}`}
            attachment={attachment}
            readOnly={readOnly || !onRemove}
            isRemoving={removingIds?.has(attachment.id) ?? false}
            onRemove={onRemove}
            translations={texts}
          />
        </li>
      ))}
    </ul>
  );
};

const AttachmentChip = ({
  attachment,
  readOnly,
  isRemoving,
  onRemove,
  translations
}: {
  attachment: NavioNoteAttachment;
  readOnly: boolean;
  isRemoving: boolean;
  onRemove?: (attachmentId: number) => void;
  translations: Translations;
}) => {
  const [previewFailed, setPreviewFailed] = useState(false);
  const isImage = attachment.kind === 'image' && !previewFailed;

  return (
    <div className='group relative flex max-w-full items-center gap-2 rounded-lg border border-border bg-background p-1.5'>
      <a
        href={attachment.url}
        target='_blank'
        rel='noopener noreferrer'
        className='flex min-w-0 items-center gap-2 rounded-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'
        aria-label={
          isImage
            ? translations.openImage(attachment.originalFilename)
            : translations.downloadDocument(attachment.originalFilename)
        }
      >
        {isImage ? (
          <img
            src={attachment.url}
            alt=''
            className='size-12 shrink-0 rounded-md object-cover'
            onError={() => setPreviewFailed(true)}
          />
        ) : (
          <span className='flex size-12 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground'>
            <NoddiIcon name='FileText' size='medium' />
          </span>
        )}
        <span className='max-w-40 truncate pr-1 text-xs font-medium'>{attachment.originalFilename}</span>
      </a>
      {!readOnly && onRemove && (
        <NoddiIconButton
          variant='ghost'
          size='small'
          iconName='X'
          aria-label={translations.removeAttachment}
          title={translations.removeAttachment}
          loading={isRemoving}
          disabled={isRemoving}
          className='shrink-0 text-muted-foreground'
          onClick={() => onRemove(attachment.id)}
        />
      )}
    </div>
  );
};
